import { WebhookStatus } from '@atlas/types'
import { EmptyState, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@atlas/ui-components'
import { Badge, type BadgeProps, Button, MenuContent, MenuItem, MenuTrigger, Spinner } from '@atlas/ui-primitives'
import { ChevronDown, ChevronRight, MoreHorizontal } from 'lucide-react'
import { Fragment } from 'react'
import { formatUtc } from '@/lib/format-utc'
import type { WebhookDTO } from '@/services/webhook.service'

/**
 * The ledger table. Presentational and prop-driven, so it is storiable and the
 * routed page owns every fetch.
 *
 * The ERROR is a column rather than a tooltip: on this page the reason a delivery
 * failed is the content, not a detail. A row expands to show the stored payload.
 */

export interface WebhookTableProps {
	rows: WebhookDTO[]
	isLoading?: boolean
	isError?: boolean
	/** Which row is expanded (`webhookId`), or null. */
	expandedId: string | null
	onToggleExpanded: (webhookId: string) => void
	onRetry: (row: WebhookDTO) => void
	/** The row currently being retried, so its action can report progress. */
	retryingId?: string | null
}

const STATUS_VARIANT: Record<string, NonNullable<BadgeProps['variant']>> = {
	[WebhookStatus.FAILED]: 'danger',
	[WebhookStatus.PROCESSED]: 'primary',
	[WebhookStatus.PROCESSING]: 'secondary',
	[WebhookStatus.RECEIVED]: 'muted',
}

/** Kept next to the header row: the expanded detail must span every column. */
const COLUMN_COUNT = 7

/** The per-row kebab. Retry is offered only where it is legal — a FAILED row. */
function RowActions({
	row,
	onRetry,
	retryingId,
}: {
	row: WebhookDTO
	onRetry: (row: WebhookDTO) => void
	retryingId?: string | null
}) {
	return (
		// Stops the row's own expand toggle from firing when the menu is used.
		<div onClick={(event) => event.stopPropagation()} onKeyDown={(event) => event.stopPropagation()}>
			<MenuTrigger>
				<Button variant="outline" size="sm" aria-label={`Actions for delivery ${row.webhookId}`}>
					<MoreHorizontal className="h-4 w-4" aria-hidden="true" />
				</Button>
				<MenuContent placement="bottom end">
					<MenuItem onAction={() => void navigator.clipboard?.writeText(row.webhookId)}>Copy delivery id</MenuItem>
					<MenuItem onAction={() => void navigator.clipboard?.writeText(JSON.stringify(row.payload, null, 2))}>
						Copy payload
					</MenuItem>
					{row.status === WebhookStatus.FAILED ? (
						<MenuItem isDisabled={retryingId === row.webhookId} onAction={() => onRetry(row)}>
							{retryingId === row.webhookId ? 'Retrying…' : 'Retry intake'}
						</MenuItem>
					) : null}
				</MenuContent>
			</MenuTrigger>
		</div>
	)
}

/**
 * The S3 prefix this delivery's raw body was archived under.
 *
 * The full key ends in a random suffix that is not stored, so it cannot be
 * reconstructed here — but the prefix narrows an `aws s3 ls` to one day of one
 * provider, which is what the runbook's lookup actually needs. Worth surfacing
 * because the archive holds bodies the ledger never sees (a malformed payload
 * never reaches a row at all).
 */
const archivePrefix = (row: WebhookDTO): string => `${row.provider}/${row.receivedAt.slice(0, 10).replace(/-/g, '/')}/`

/** The expanded row: ids, timings, the archive prefix, and the payload as stored. */
function RowDetail({ row }: { row: WebhookDTO }) {
	return (
		<TableCell colSpan={COLUMN_COUNT} className="bg-ds-surface">
			<dl className="space-y-1 text-xs">
				<div className="flex gap-2">
					<dt className="w-28 shrink-0 font-mono text-ds-text-muted">delivery id</dt>
					<dd className="font-mono">{row.webhookId}</dd>
				</div>
				{row.processedAt ? (
					<div className="flex gap-2">
						<dt className="w-28 shrink-0 font-mono text-ds-text-muted">processed</dt>
						<dd className="font-mono">{formatUtc(row.processedAt)}</dd>
					</div>
				) : null}
				<div className="flex gap-2">
					<dt className="w-28 shrink-0 font-mono text-ds-text-muted">archive</dt>
					<dd className="font-mono">s3://…/{archivePrefix(row)}</dd>
				</div>
			</dl>
			<p className="mt-3 mb-1 font-mono text-ds-2xs text-ds-text-muted uppercase tracking-wide">payload</p>
			{/* The body as stored. The S3 archive holds the same bytes for 90 days;
			    see the runbook for reading one back by key. */}
			<pre className="max-h-72 overflow-auto rounded-ds-base bg-ds-surface-elevated p-3 text-xs">
				{JSON.stringify(row.payload, null, 2)}
			</pre>
		</TableCell>
	)
}

export function WebhookTable({
	rows,
	isLoading,
	isError,
	expandedId,
	onToggleExpanded,
	onRetry,
	retryingId,
}: WebhookTableProps) {
	if (isLoading) {
		return (
			<div className="flex items-center justify-center py-12" aria-busy="true">
				<Spinner size="lg" label="Loading webhooks…" />
			</div>
		)
	}

	if (isError) {
		return (
			<p role="alert" className="py-8 text-center text-ds-danger text-sm">
				Could not load the webhook ledger. Please try again.
			</p>
		)
	}

	if (rows.length === 0) {
		return <EmptyState title="No deliveries match these filters." variant="filtered" />
	}

	return (
		<Table>
			<TableHeader>
				<TableRow>
					<TableHead>Received (UTC)</TableHead>
					<TableHead>Provider</TableHead>
					<TableHead>Status</TableHead>
					<TableHead>Tries</TableHead>
					<TableHead>Error</TableHead>
					<TableHead>Actions</TableHead>
				</TableRow>
			</TableHeader>
			<TableBody>
				{rows.map((row) => (
					<Fragment key={row.webhookId}>
						<TableRow className="cursor-pointer" onClick={() => onToggleExpanded(row.webhookId)}>
							<TableCell>
								{/* A real button, not just a clickable row: a `role="button"` on a
								    `<tr>` fights its own `row` role, and without this the payload —
								    the point of the page — is unreachable by keyboard. */}
								{/* The wrapper stops the row's own click from also toggling —
								    a PressEvent has no stopPropagation, so the DOM event is
								    contained here, same pattern as the dropdown above. */}
								<span onClick={(event) => event.stopPropagation()} onKeyDown={(event) => event.stopPropagation()}>
									<Button
										variant="ghost"
										size="sm"
										aria-expanded={expandedId === row.webhookId}
										aria-label={`${expandedId === row.webhookId ? 'Collapse' : 'Expand'} delivery ${row.webhookId}`}
										onPress={() => onToggleExpanded(row.webhookId)}
									>
										{expandedId === row.webhookId ? (
											<ChevronDown className="h-4 w-4" aria-hidden="true" />
										) : (
											<ChevronRight className="h-4 w-4" aria-hidden="true" />
										)}
									</Button>
								</span>
							</TableCell>
							<TableCell className="font-mono text-xs">{formatUtc(row.receivedAt)}</TableCell>
							<TableCell>{row.provider}</TableCell>
							<TableCell>
								<Badge variant={STATUS_VARIANT[row.status] ?? 'muted'} size="sm">
									{row.status}
								</Badge>
							</TableCell>
							<TableCell className="text-ds-text-muted">{row.attempts}</TableCell>
							<TableCell className="max-w-md text-ds-danger text-sm">{row.errorMessage ?? '—'}</TableCell>
							<TableCell>
								<RowActions row={row} onRetry={onRetry} retryingId={retryingId} />
							</TableCell>
						</TableRow>
						{expandedId === row.webhookId ? (
							<TableRow>
								<RowDetail row={row} />
							</TableRow>
						) : null}
					</Fragment>
				))}
			</TableBody>
		</Table>
	)
}
